/**
 * API Key Manager for container custom API keys.
 *
 * Provides:
 * - showApiKeySetup(token) — First-load setup form (blocks until at least one key is saved)
 * - showApiKeyManager() — Modal for viewing/editing keys (from admin panel footer)
 */
import { getHeaders } from './api.js';
import { isContainerMode } from './state.js';
import { state } from './state.js';

const PROVIDERS = [
    { id: 'anthropic', name: 'Anthropic', placeholder: 'sk-ant-...', prefix: 'sk-ant-' },
    { id: 'openai', name: 'OpenAI', placeholder: 'sk-...', prefix: 'sk-' },
    { id: 'groq', name: 'Groq', placeholder: 'gsk_...', prefix: 'gsk_' },
    { id: 'openrouter', name: 'OpenRouter', placeholder: 'sk-or-...', prefix: 'sk-or-' }
];

/**
 * Show the first-load API key setup form.
 * Returns a promise that resolves when at least one key is saved.
 */
export function showApiKeySetup(token) {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'api-key-overlay';
        overlay.innerHTML = `
            <div class="api-key-setup">
                <div class="api-key-setup-header">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                    </svg>
                    <h2>API Key Setup</h2>
                </div>
                <p class="api-key-setup-desc">
                    Enter API keys to enable cloud LLM providers.
                    Keys are encrypted and stored securely. You can skip this to use local models only.
                </p>
                <div class="api-key-fields">
                    ${PROVIDERS.map(p => `
                        <div class="api-key-field" data-provider="${p.id}">
                            <label>${p.name}</label>
                            <div class="api-key-input-row">
                                <input type="password" placeholder="${p.placeholder}" autocomplete="off" spellcheck="false">
                                <button class="api-key-toggle-vis" title="Show/hide key">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                </button>
                                <button class="api-key-test-btn" title="Test key" disabled>Test</button>
                            </div>
                            <div class="api-key-status"></div>
                        </div>
                    `).join('')}
                </div>
                <div class="api-key-setup-actions">
                    <button class="api-key-save-btn">Continue</button>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        const saveBtn = overlay.querySelector('.api-key-save-btn');
        const fields = overlay.querySelectorAll('.api-key-field');

        // Toggle visibility
        fields.forEach(field => {
            const input = field.querySelector('input');
            const toggleBtn = field.querySelector('.api-key-toggle-vis');
            const testBtn = field.querySelector('.api-key-test-btn');

            toggleBtn.addEventListener('click', () => {
                input.type = input.type === 'password' ? 'text' : 'password';
            });

            input.addEventListener('input', () => {
                testBtn.disabled = !input.value.trim();
                field.querySelector('.api-key-status').textContent = '';
            });

            testBtn.addEventListener('click', async () => {
                const key = input.value.trim();
                if (!key) return;
                const provider = field.dataset.provider;
                const statusEl = field.querySelector('.api-key-status');

                testBtn.disabled = true;
                testBtn.textContent = '...';
                statusEl.textContent = 'Testing...';
                statusEl.className = 'api-key-status testing';

                try {
                    // Test key directly without saving
                    const res = await fetch(`/container/api-keys/test/${provider}`, {
                        method: 'POST',
                        headers: getHeaders(),
                        body: JSON.stringify({ api_key: key })
                    });
                    const data = await res.json();
                    if (data.success) {
                        statusEl.textContent = 'Valid';
                        statusEl.className = 'api-key-status success';
                    } else {
                        statusEl.textContent = data.error || 'Invalid key';
                        statusEl.className = 'api-key-status error';
                    }
                } catch (e) {
                    statusEl.textContent = 'Test failed';
                    statusEl.className = 'api-key-status error';
                }
                testBtn.textContent = 'Test';
                testBtn.disabled = false;
            });
        });

        // Save all keys
        saveBtn.addEventListener('click', async () => {
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving...';

            let savedCount = 0;
            for (const field of fields) {
                const key = field.querySelector('input').value.trim();
                if (!key) continue;
                const provider = field.dataset.provider;

                try {
                    await fetch(`/container/api-keys/${provider}`, {
                        method: 'PUT',
                        headers: getHeaders(),
                        body: JSON.stringify({ api_key: key })
                    });
                    savedCount++;
                } catch (e) {
                    console.error(`Failed to save ${provider} key:`, e);
                }
            }

            overlay.remove();
            resolve();
        });
    });
}

/**
 * Show the API key manager modal (for editing existing keys).
 * Called from the admin panel footer icon.
 */
export async function showApiKeyManager() {
    if (!isContainerMode() || !state.containerConfig?.custom_api) return;

    const token = state.containerToken;

    // Fetch current masked keys
    let maskedKeys = {};
    try {
        const res = await fetch('/container/api-keys', { headers: getHeaders() });
        const data = await res.json();
        maskedKeys = data.keys || {};
    } catch (e) {
        console.error('Failed to fetch API keys:', e);
    }

    const overlay = document.createElement('div');
    overlay.className = 'api-key-overlay';
    overlay.innerHTML = `
        <div class="api-key-modal">
            <div class="api-key-modal-header">
                <h3>API Key Manager</h3>
                <button class="api-key-close-btn" title="Close">&times;</button>
            </div>
            <div class="api-key-fields">
                ${PROVIDERS.map(p => {
                    const masked = maskedKeys[p.id] || '';
                    const hasKey = !!masked;
                    return `
                        <div class="api-key-field" data-provider="${p.id}">
                            <label>${p.name} ${hasKey ? '<span class="api-key-badge">configured</span>' : ''}</label>
                            <div class="api-key-input-row">
                                <input type="password" placeholder="${hasKey ? masked : p.placeholder}" autocomplete="off" spellcheck="false">
                                <button class="api-key-toggle-vis" title="Show/hide">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                </button>
                                <button class="api-key-update-btn" disabled>Update</button>
                                ${hasKey ? '<button class="api-key-delete-btn" title="Remove key">Remove</button>' : ''}
                            </div>
                            <div class="api-key-status"></div>
                        </div>
                    `;
                }).join('')}
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    // Close
    overlay.querySelector('.api-key-close-btn').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

    // Field interactions
    const fields = overlay.querySelectorAll('.api-key-field');
    fields.forEach(field => {
        const input = field.querySelector('input');
        const toggleBtn = field.querySelector('.api-key-toggle-vis');
        const updateBtn = field.querySelector('.api-key-update-btn');
        const deleteBtn = field.querySelector('.api-key-delete-btn');
        const statusEl = field.querySelector('.api-key-status');
        const provider = field.dataset.provider;

        toggleBtn.addEventListener('click', () => {
            input.type = input.type === 'password' ? 'text' : 'password';
        });

        input.addEventListener('input', () => {
            updateBtn.disabled = !input.value.trim();
            statusEl.textContent = '';
        });

        updateBtn.addEventListener('click', async () => {
            const key = input.value.trim();
            if (!key) return;
            updateBtn.disabled = true;
            updateBtn.textContent = '...';

            try {
                await fetch(`/container/api-keys/${provider}`, {
                    method: 'PUT',
                    headers: getHeaders(),
                    body: JSON.stringify({ api_key: key })
                });
                statusEl.textContent = 'Updated';
                statusEl.className = 'api-key-status success';
                input.value = '';
                input.placeholder = key.substring(0, 3) + '...' + key.slice(-4);

                // Show configured badge
                const label = field.querySelector('label');
                if (!label.querySelector('.api-key-badge')) {
                    label.insertAdjacentHTML('beforeend', ' <span class="api-key-badge">configured</span>');
                }
            } catch (e) {
                statusEl.textContent = 'Failed to save';
                statusEl.className = 'api-key-status error';
            }
            updateBtn.textContent = 'Update';
            updateBtn.disabled = true;
        });

        if (deleteBtn) {
            deleteBtn.addEventListener('click', async () => {
                if (!confirm(`Remove ${PROVIDERS.find(p => p.id === provider)?.name} API key?`)) return;
                try {
                    await fetch(`/container/api-keys/${provider}`, {
                        method: 'DELETE',
                        headers: getHeaders()
                    });
                    statusEl.textContent = 'Removed';
                    statusEl.className = 'api-key-status';
                    input.placeholder = PROVIDERS.find(p => p.id === provider)?.placeholder || '';
                    deleteBtn.remove();
                    const badge = field.querySelector('.api-key-badge');
                    if (badge) badge.remove();
                } catch (e) {
                    statusEl.textContent = 'Failed to remove';
                    statusEl.className = 'api-key-status error';
                }
            });
        }
    });
}
